import { Box, Link as MuiLink, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLogin } from "providers/Login";
import { useNavigate } from "react-router";
import { useNotification } from "providers/Notification";
import DataTable, { GetSortString, useTableColumnState } from "components/Table";
import StatusLabel, { StatusColor } from "components/StatusLabel";
import { formatDateTime, fromUnix } from "helpers/dateFormat";

interface AdminUserModel {
    items: AdminUserItem[]
}

interface AdminUserItem {
    id: string
    username: string
    lastLoginTimestamp: Date
    status: string
}

type columnsType = "username" | "lastLoginTimestamp" | "status";

export default function AdminUsers() {
    const navigate = useNavigate();
    const login = useLogin();
    const notification = useNotification();
    const [finishedLoading, setFinishedLoading] = useState(false);

    const [skipItems, setSkipItems] = useState(0);
    const [takeCount, setTakeCount] = useState(25);
    const [dataCount, setDataCount] = useState(0);

    const [columnsState, setColumnsState] = useTableColumnState<columnsType>([
        { columnId: "username", width: "50%", sortOrder: 1, direction: "asc" },
        { columnId: "lastLoginTimestamp", width: "30%", sortOrder: 2, direction: "desc" },
        { columnId: "status", width: "20%" },
    ]);

    const [listState, setListState] = useState<AdminUserModel>({ items: [] });

    const req = login.GetAxios();

    // Guards against out-of-order responses: if the user changes sort/page
    // again before an earlier request resolves, only the latest request's
    // response is allowed to update state.
    const latestRequestId = useRef(0);

    const loadData = async (c: typeof columnsState, skipItems: number, takeCount: number) => {
        const requestId = ++latestRequestId.current;
        setFinishedLoading(false);
        if (!req) {
            setFinishedLoading(true);
            return; //not logged in, nothing to do
        }
        try {
            let sortBy = GetSortString(c);

            const resp = await req.get("/api/adminusers", {
                params: {
                    sortBy,
                    skip: skipItems,
                    take: takeCount,
                }
            });

            if (requestId !== latestRequestId.current) {
                return; // a newer request started; ignore this stale response
            }

            setSkipItems(skipItems);
            setTakeCount(takeCount);
            setColumnsState(c);
            setListState({
                items: (resp.data.items || []).map((x: any) => {
                    const item: AdminUserItem = {
                        id: x.username,
                        username: x.username,
                        lastLoginTimestamp: fromUnix(x.lastLoginTimestamp),
                        status: x.status,
                    };
                    return item;
                })
            });
            setDataCount(resp.data.count);
        } catch (err) {
            if (requestId !== latestRequestId.current) {
                return; // a newer request started; ignore this stale response
            }

            notification.Display({
                title: "Error",
                message: "Could not fetch admin user data",
                type: "error",
            });
        } finally {
            if (requestId === latestRequestId.current) {
                setFinishedLoading(true);
            }
        }
    };

    useEffect(() => {
        loadData(columnsState, skipItems, takeCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderStatus = (status: string) => {
        let color: StatusColor = "error";
        if (status === "Active") color = "success";
        else if (status === "Logged Out") color = "disabled";

        return <StatusLabel label={status} color={color} />;
    };

    return <>
        <Box display="flex">
            <Typography variant="h1">Administrator Sessions</Typography>
        </Box>

        <DataTable
            sx={{
                maxHeight: "90%",
                marginTop: "10px",
            }}
            size="medium"
            columnsDataId={"adminUsersTablev1"}
            data={listState.items}
            dataCount={dataCount}
            finishedLoading={finishedLoading}
            paginationStartItem={skipItems}
            paginationItemCount={takeCount}
            onPaginationStartItemChanged={(x) => {
                loadData(columnsState, x, takeCount);
            }}
            onPaginationItemCountChanged={(x) => {
                loadData(columnsState, skipItems, x);
            }}
            onSortChanged={(x) => loadData(x, skipItems, takeCount)}
            onColumnOrderChanged={(x) => setColumnsState(x)}
            columnsState={columnsState}
            columns={{
                username: {
                    displayName: "Username",
                    sortable: true,
                    render: (x) => {
                        return (
                            <MuiLink
                                component="button"
                                variant="body2"
                                onClick={() => navigate(`/adminlogins/${encodeURIComponent(x.username)}`)}
                            >
                                {x.username}
                            </MuiLink>
                        );
                    },
                },
                lastLoginTimestamp: {
                    displayName: "Last Login Time",
                    sortable: true,
                    render: (x) => {
                        return formatDateTime(x.lastLoginTimestamp);
                    },
                },
                status: {
                    displayName: "Status",
                    sortable: false,
                    render: (x) => {
                        return renderStatus(x.status);
                    },
                },
            }}
        />
    </>;
}
